import type { IAskSuggestions, IAskSuggestQuestion } from "searchAsk/askSuggest";
import { countKey } from "searchAsk/useAskSuggest";

import { IconSparkle } from "@btnet/blueprint-icons";
import { BTText } from "@btnet/core-components/BTText";
import { cn } from "@btnet/utilities-core";

import { getEntityMeta } from "commonComponents/utilities/MainNavigation/AiSearch/entityMeta";

import type { ISearchSuggestion } from "./results/useSearchSuggest";
import { SearchDialogRow } from "./rows/SearchDialogRow";
import type { ISearchDialogRow } from "./rows/searchDialogRow.types";

/** Its listbox, named in the input's `aria-controls` beside the results'. */
export const SEARCH_DIALOG_SUGGESTIONS_ID = "search-dialog-suggestions";
/** Its options' ids, by place: these rows come first in the dialog's row order. */
export const SEARCH_DIALOG_SUGGESTION_ID_PREFIX = "search-dialog-suggestion-";

/** With nothing typed there are no results to make room for. */
const MAX_IDLE = 4;

/**
 * What needs attention across the account, each with its count, offered with
 * nothing typed: picking one asks it on the Ask page.
 */
export function attentionRows(
    suggestions: IAskSuggestions,
    counts: ReadonlyMap<string, number | null>,
    onAsk: (question: IAskSuggestQuestion, rank: number, shown: number) => void,
): ISearchDialogRow[] {
    // Only a count read as zero takes a row away: one that timed out or
    // failed (null) keeps its row, without a count.
    const shown = suggestions.questions
        .filter((q) => q.query === null || counts.get(countKey(q.query)) !== 0)
        .slice(0, MAX_IDLE);
    return shown.map((q, index) => {
        const count = q.query === null ? undefined : counts.get(countKey(q.query));
        return {
            key: `suggest:${q.text}`,
            kind: "prompt" as const,
            icon: <IconSparkle />,
            title: q.text,
            subtitle: null,
            amount: null,
            status: null,
            count: typeof count === "number" && count > 0 ? { value: count, tone: q.tone } : null,
            trailingLabel: null,
            onSelect: () => onAsk(q, index + 1, shown.length),
        };
    });
}

export interface ISearchDialogSuggestionsProps {
    /** While typing: the records the text could be. */
    names: readonly ISearchSuggestion[];
    /**
     * The dialog's rows for what is shown here, first in its row order: one
     * per name while typing, else what needs attention. A row's `onSelect` is
     * what Enter and a click do.
     */
    rows: readonly ISearchDialogRow[];
    /** Index into the dialog's row order. */
    highlightedIndex: number;
    onHoverRow: (index: number) => void;
}

/** Rows above the results, first in keyboard order: typeahead names while typing, needs-attention prompts when empty. */
export function SearchDialogSuggestions({
    names,
    rows,
    highlightedIndex,
    onHoverRow,
}: ISearchDialogSuggestionsProps) {
    if (names.length > 0) {
        return (
            <div
                role="listbox"
                id={SEARCH_DIALOG_SUGGESTIONS_ID}
                aria-label="Search suggestions"
                className="shrink-0 border-b border-default py-1"
                data-testid="search-dialog-suggestions"
            >
                {names.map((suggestion, index) => {
                    const highlighted = index === highlightedIndex;
                    return (
                        <div
                            key={`${suggestion.entityType}:${suggestion.id}:${suggestion.text}`}
                            id={`${SEARCH_DIALOG_SUGGESTION_ID_PREFIX}${index}`}
                            role="option"
                            aria-selected={highlighted}
                            // The input owns the keyboard (combobox); a row is
                            // reached by active-descendant, never by focus.
                            tabIndex={-1}
                            className={cn(
                                "flex cursor-pointer items-center gap-2 rounded-md px-3 py-1",
                                highlighted
                                    ? "bg-surface-selected"
                                    : "hover:bg-surface-default-hover",
                            )}
                            onMouseMove={() => onHoverRow(index)}
                            onClick={() => rows[index]?.onSelect?.("click")}
                            data-testid="search-dialog-row"
                            data-row-kind="search"
                        >
                            <BTText
                                size="normal-sm"
                                tag="span"
                                className="min-w-24 shrink-0 uppercase text-fg-tertiary"
                            >
                                {getEntityMeta(suggestion.entityType).label}
                            </BTText>
                            <BTText
                                size="normal-md"
                                tag="span"
                                className="min-w-0 truncate text-fg-default"
                            >
                                {suggestion.text}
                            </BTText>
                        </div>
                    );
                })}
            </div>
        );
    }
    if (rows.length === 0) {
        return null;
    }
    return (
        <div
            role="listbox"
            id={SEARCH_DIALOG_SUGGESTIONS_ID}
            aria-label="Needs attention"
            className="shrink-0 border-b border-default py-2"
            data-testid="search-dialog-suggestions"
        >
            <BTText
                size="normal-sm"
                tag="div"
                className="px-2 pb-2 pt-2 text-fg-tertiary"
                aria-hidden="true"
            >
                Needs attention
            </BTText>
            {rows.map((row, index) => (
                <SearchDialogRow
                    key={row.key}
                    row={row}
                    role="option"
                    elementId={`${SEARCH_DIALOG_SUGGESTION_ID_PREFIX}${index}`}
                    isHighlighted={index === highlightedIndex}
                    onHover={() => onHoverRow(index)}
                    compact
                />
            ))}
        </div>
    );
}
